import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'drift-ui';
import {
  BankingApplicant,
  BankingApplicationDocument,
  DO_APPLICATION_TYPE,
  EntityStatus,
  KYBStatus,
  KYCStatus,
  SubmissionSteps,
  buildUpdatedSubmissionSteps,
  type BackgroundCheckResult,
} from 'client-experience-toolbox';
import cx from 'classnames';
import alloy from '@alloyidentity/web-sdk';
import { SET_BANKING_APPLICATION } from 'app/actionTypes';
import baseStyles from './BaseFormStyles.scss';
import styles from './BankingPostSubmissionDisplay.scss';
import { useApplication } from 'hooks/useApplication';
import { initAlloy } from '../../utils/utils';
import { BankingErrorScreen } from './BankingErrorScreen';
import { RootState } from 'app/configureStore';
import isEqual from 'lodash/isEqual';

type PostSubmissionState = 'denied' | 'manualReview' | 'bothStepUp' | 'primaryOnlyStepUp' | 'secondaryOnlyStepUp';

type AlloyMessageEvent = BackgroundCheckResult & {
  sdk?: {
    sdkEvent: string;
  };
  status?: string;
};

const userUpdatedAlloy = (current: SubmissionSteps, updated: SubmissionSteps) => {
  const kycChanged = !isEqual(current.kyc, updated.kyc);
  const kycStatusChanged = current.kycStatus !== updated.kycStatus;
  const kybStatusChanged = current.kybStatus !== updated.kybStatus;
  return kycChanged || kycStatusChanged || kybStatusChanged;
};

const stateMessageMap = {
  denied: {
    messageLineOne: 'digital_onboarding.banking.post_submission_screens.denied_text',
    messageLineTwo: 'digital_onboarding.banking.post_submission_screens.link_external_bank',
    buttonText: 'Close',
  },
  manualReview: {
    messageLineOne: 'digital_onboarding.banking.post_submission_screens.additional_time',
    messageLineTwo: 'digital_onboarding.banking.post_submission_screens.lookout_for_email',
    buttonText: 'Close',
  },
  bothStepUp: {
    messageLineOne: 'digital_onboarding.banking.post_submission_screens.ask_more_questions',
    messageLineTwo: 'digital_onboarding.banking.post_submission_screens.we_will_also_reach_out',
    buttonText: 'Next',
  },
  primaryOnlyStepUp: {
    messageLineOne: 'digital_onboarding.banking.post_submission_screens.ask_more_questions',
    messageLineTwo: '',
    buttonText: 'Next',
  },
  secondaryOnlyStepUp: {
    messageLineOne: 'digital_onboarding.banking.post_submission_screens.details_verification_needed',
    messageLineTwo: 'digital_onboarding.banking.post_submission_screens.we_will_reach_out',
    buttonText: 'Next',
  },
};

const PRIMARY_ALLOWED_STEP_UP_STATES = ['bothStepUp', 'primaryOnlyStepUp', 'secondaryOnlyStepUp'];

const getStepUpState = (
  primaryApplicantKYC?: EntityStatus,
  pendingUsers?: Partial<BankingApplicant>[],
): PostSubmissionState => {
  if (primaryApplicantKYC?.status === KYCStatus.STEP_UP) {
    if (pendingUsers && pendingUsers.length > 0) {
      return 'bothStepUp';
    } else {
      return 'primaryOnlyStepUp';
    }
  } else {
    return 'secondaryOnlyStepUp';
  }
};

const ALLOY_CONTAINER_ID = 'alloy-step-up-container';

export const BankingPostSubmissionDisplay: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bankingSubmissionError = useSelector((state: RootState) => state.applicationReducer.bankingSubmissionError);
  const { application } = useApplication(DO_APPLICATION_TYPE.BANKING) as { application: BankingApplicationDocument };
  const submissionSteps = (application as BankingApplicationDocument)?.submissionSteps;
  const { kybStatus, kycStatus, journeyApplicationToken } = submissionSteps ?? {};
  const primaryApplicantKYC = submissionSteps?.kyc?.find(
    (applicant) => applicant.email === application?.applicationData?.applicants?.[0]?.email,
  );

  let pendingUsers: Array<Partial<BankingApplicant>> | undefined;
  if (kycStatus === KYCStatus.STEP_UP) {
    const controlProng = application?.applicationData?.controlProng;
    const eligibleApplicants = [
      ...(application?.applicationData?.applicants || []),
      controlProng?.key === 'controlProng' ? controlProng : null,
    ].filter(Boolean) as Array<Partial<BankingApplicant>>;

    pendingUsers = eligibleApplicants.filter((applicant) =>
      submissionSteps?.kyc?.find(
        (kyc) => kyc.status === KYCStatus.STEP_UP && kyc.email === applicant.email && !applicant.applicantId,
      ),
    );
  }

  useEffect(() => {
    initAlloy(journeyApplicationToken);
  }, [journeyApplicationToken]);

  const retryBackgroundCheck = async () => {
    dispatch({ type: 'SET_BANKING_SUBMISSION_ERROR', bankingSubmissionError: false });
    dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'submitting' });
  };

  let postSubmissionState: PostSubmissionState = 'primaryOnlyStepUp';

  if (kybStatus === KYBStatus.DENIED || kycStatus === KYCStatus.DENIED) {
    postSubmissionState = 'denied';
  } else if (submissionSteps?.kycStatus === KYCStatus.APPROVED && submissionSteps?.kybStatus === KYBStatus.APPROVED) {
    return <Navigate to="/banking" />;
  } else if (submissionSteps?.kycStatus === KYCStatus.STEP_UP) {
    postSubmissionState = getStepUpState(primaryApplicantKYC, pendingUsers);
  } else if (submissionSteps?.kybStatus === KYBStatus.STEP_UP) {
    postSubmissionState = 'primaryOnlyStepUp';
  } else {
    postSubmissionState = 'manualReview';
  }

  if (bankingSubmissionError) {
    return <BankingErrorScreen onRefresh={retryBackgroundCheck} />;
  }

  const { messageLineOne, messageLineTwo, buttonText } = stateMessageMap[postSubmissionState];

  const handleAlloyReturnState = (data: AlloyMessageEvent) => {
    if (!submissionSteps) {
      console.error('Submission steps data is not available');
      return;
    }

    const updatedSubmissionSteps = data._embedded && buildUpdatedSubmissionSteps(data, submissionSteps);
    const sdkEvent = data?.sdk?.sdkEvent;
    if (data.status === 'closed' || sdkEvent === 'exit') {
      navigate('/banking');
    } else if (updatedSubmissionSteps && userUpdatedAlloy(submissionSteps, updatedSubmissionSteps)) {
      dispatch({
        type: SET_BANKING_APPLICATION,
        bankingApplication: {
          ...application,
          submissionSteps: updatedSubmissionSteps,
        },
        sectionHasChanged: false,
      });

      if (updatedSubmissionSteps.kycStatus === KYCStatus.APPROVED) {
        dispatch({ type: 'SET_POST_SUBMISSION_STEP', submissionStep: 3 });
        dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'submitting' });
      }
    } else {
      navigate('/banking');
    }
  };

  const onHandleAction = () => {
    if (PRIMARY_ALLOWED_STEP_UP_STATES.includes(postSubmissionState)) {
      alloy.open(handleAlloyReturnState, ALLOY_CONTAINER_ID);
      return;
    }

    navigate('/banking');
  };

  return (
    <div className={styles.postSubmissionContainer}>
      <div className={styles.descriptionText}>
        <p className={styles.leadText}>
          {t('digital_onboarding.banking.post_submission_screens.thank_you_for_submitting')}
        </p>
        <div
          className={cx(
            styles.secondaryTextContainer,
            (pendingUsers || postSubmissionState === 'manualReview') && styles.pendingSecondaryTextContainer,
          )}
        >
          <p className={styles.secondText}>{t(messageLineOne)}</p>
          {messageLineTwo && <p className={styles.secondText}>{t(messageLineTwo)}</p>}
          {pendingUsers && (
            <div className={styles.pendingUserTextContainer}>
              {pendingUsers.map((user) => (
                <div className={styles.pendingUserRow} key={user.applicantId}>
                  <p className={styles.leadText}>
                    {user.firstName} {user.lastName}
                  </p>
                  <p>{user.email}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={styles.buttonContainer}>
        <Button className={baseStyles.buttonStyle} variant="contained" onClick={onHandleAction}>
          {t(buttonText)}
        </Button>
      </div>
      <div id={ALLOY_CONTAINER_ID} className={styles.stepUpContainer} />
    </div>
  );
};
